import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import CategoryManager from './CategoryManager';
import WMSLayerManager from './WMSLayerManager';
import './AdminDashboard.css';

interface User {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: any;
  lastLogin: any;
  drawingsCount: number;
  wmsLayersCount: number;
}

const AdminDashboard = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = await user?.getIdToken();

      console.log('Fetching users from:', `${import.meta.env.VITE_API_URL}/api/users`);
      console.log('Token present:', !!token);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('API Error:', errorData);
        throw new Error(errorData.error || 'Failed to fetch users');
      }

      const data = await response.json();
      console.log('Fetched users:', data);
      setUsers(data.users);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const token = await user?.getIdToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/users/${userId}/role`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ role: newRole }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      await fetchUsers();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const updateUserStatus = async (userId: string, isActive: boolean) => {
    try {
      const token = await user?.getIdToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/users/${userId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      await fetchUsers();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const clearUserDrawings = async (userId: string, userEmail: string, drawingsCount: number) => {
    if (drawingsCount === 0) {
      alert('This user has no drawings to clear.');
      return;
    }

    if (!confirm(`Are you sure you want to delete all ${drawingsCount} drawing(s) by ${userEmail}? This cannot be undone.`)) {
      return;
    }

    try {
      const token = await user?.getIdToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/users/${userId}/drawings`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to clear user drawings');
      }

      const data = await response.json();
      alert(data.message);
      await fetchUsers();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading admin dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-container">
        <h1>Admin Dashboard</h1>

        {error && <div className="error-message">{error}</div>}

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Users</h3>
            <p className="stat-number">{users.length}</p>
          </div>
          <div className="stat-card">
            <h3>Active Users</h3>
            <p className="stat-number">
              {users.filter(u => u.isActive).length}
            </p>
          </div>
          <div className="stat-card">
            <h3>Admins</h3>
            <p className="stat-number">
              {users.filter(u => u.role === 'admin').length}
            </p>
          </div>
          <div className="stat-card">
            <h3>Total Drawings</h3>
            <p className="stat-number">
              {users.reduce((sum, u) => sum + u.drawingsCount, 0)}
            </p>
          </div>
        </div>

        <div className="users-section">
          <h2>User Management</h2>
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Drawings</th>
                  <th>WMS Layers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.displayName}</td>
                    <td>
                      <span className={`badge badge-${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${u.isActive ? 'active' : 'inactive'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{u.drawingsCount}</td>
                    <td>{u.wmsLayersCount}</td>
                    <td>
                      <div className="action-buttons">
                        {u.role === 'user' ? (
                          <button
                            onClick={() => updateUserRole(u.id, 'admin')}
                            className="btn-small btn-promote"
                          >
                            Promote
                          </button>
                        ) : (
                          <button
                            onClick={() => updateUserRole(u.id, 'user')}
                            className="btn-small btn-demote"
                          >
                            Demote
                          </button>
                        )}
                        <button
                          onClick={() => updateUserStatus(u.id, !u.isActive)}
                          className="btn-small btn-toggle"
                        >
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => clearUserDrawings(u.id, u.email, u.drawingsCount)}
                          className="btn-small btn-danger"
                          disabled={u.drawingsCount === 0}
                        >
                          Clear Drawings
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="layers-management-section">
          <div className="management-grid">
            <CategoryManager />
            <WMSLayerManager />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
